/**
 * CR-3409: PO Line Communication Log
 * CR-2220: Strikethrough and delete message functionality
 * Displays conversation thread for a PO Line with send, read, complete,
 * strikethrough, and delete functionality
 */
import { useEffect, useState, useRef } from 'react';
import {
  MessageSquare,
  Send,
  CheckCircle,
  Check,
  CheckCheck,
  Loader2,
  AlertCircle,
  MoreVertical,
  Strikethrough,
  Trash2,
  Undo2,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { purchasingAPI, POLineMessage } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { useFormatters } from '../../contexts/PreferencesContext';

interface CommunicationLogProps {
  lineId: number;
  poId: number;
  partNumber?: string;
}

export default function CommunicationLog({ lineId, poId, partNumber }: CommunicationLogProps) {
  const { user } = useAuth();
  const { formatDateTime } = useFormatters();
  const [messages, setMessages] = useState<POLineMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchMessages();
    // Mark all as read when opening
    purchasingAPI.markAllMessagesRead(lineId).catch((err: unknown) => console.error('Failed to mark messages read:', err));
  }, [lineId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await purchasingAPI.getLineMessages({ po_line: lineId, page_size: 100 });
      setMessages(response.data.results || []);
    } catch (err) {
      setError('Failed to load communications');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    try {
      setSending(true);
      await purchasingAPI.createLineMessage({
        po: poId,
        po_line: lineId,
        message_text: newMessage.trim(),
      });
      setNewMessage('');
      await fetchMessages();
      toast.success('Message sent');
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleMarkComplete = async (msgId: number) => {
    try {
      await purchasingAPI.markMessageComplete(msgId);
      await fetchMessages();
      toast.success('Marked as complete');
    } catch (err) {
      toast.error('Failed to mark complete');
    }
  };

  // CR-2220: Strikethrough handler
  const handleStrikethrough = async (msgId: number) => {
    try {
      setActionLoading(msgId);
      await purchasingAPI.strikethroughLineMessage(msgId);
      await fetchMessages();
      toast.success('Message struck through');
    } catch (err) {
      toast.error('Failed to strikethrough message');
    } finally {
      setActionLoading(null);
    }
  };

  // CR-2220: Remove strikethrough handler
  const handleRemoveStrikethrough = async (msgId: number) => {
    try {
      setActionLoading(msgId);
      await purchasingAPI.removeStrikethroughLineMessage(msgId);
      await fetchMessages();
      toast.success('Strikethrough removed');
    } catch (err) {
      toast.error('Failed to remove strikethrough');
    } finally {
      setActionLoading(null);
    }
  };

  // CR-2220: Delete message handler
  const handleDeleteMessage = async (msgId: number) => {
    try {
      setActionLoading(msgId);
      await purchasingAPI.deleteLineMessage(msgId);
      await fetchMessages();
      toast.success('Message deleted');
    } catch (err) {
      toast.error('Failed to delete message');
    } finally {
      setActionLoading(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
        <p className="text-sm text-destructive">{error}</p>
        <button onClick={fetchMessages} className="text-sm text-primary hover:underline mt-2">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[400px]">
      {/* Header */}
      <div className="flex items-center gap-2 pb-3 border-b">
        <MessageSquare className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">
          Communication Log {partNumber ? `— ${partNumber}` : ''}
        </span>
        <span className="text-xs text-muted-foreground ml-auto">
          {messages.length} message{messages.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-3 space-y-3">
        {messages.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="h-10 w-10 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No communications yet</p>
            <p className="text-xs text-muted-foreground mt-1">Start a conversation about this PO line</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isOwn = msg.sender === user?.id;
            const isLoading = actionLoading === msg.id;
            return (
              <div
                key={msg.id}
                className={`flex group ${isOwn ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex items-start gap-1 max-w-[75%] ${isOwn ? 'flex-row-reverse' : ''}`}>
                  <div
                    className={`rounded-lg px-3 py-2 ${
                      isOwn
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted'
                    } ${msg.is_complete || msg.is_strikethrough ? 'opacity-60' : ''}`}
                  >
                    {!isOwn && (
                      <p className="text-xs font-medium mb-1 opacity-70">
                        {msg.sender_details?.full_name || 'Unknown'}
                      </p>
                    )}
                    <p className={`text-sm whitespace-pre-wrap ${msg.is_strikethrough ? 'line-through' : ''}`}>
                      {msg.message_text}
                    </p>
                    {/* CR-2220: Show strikethrough indicator */}
                    {msg.is_strikethrough && (
                      <p className="text-[10px] mt-1 italic opacity-70">
                        Struck through by {msg.strikethrough_by_name}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] opacity-60">
                        {formatDateTime(msg.sent_date)}
                      </span>
                      {msg.is_complete && (
                        <span className="text-[10px] opacity-60 flex items-center gap-0.5">
                          <CheckCircle className="h-3 w-3" /> Complete
                        </span>
                      )}
                      {msg.is_read && !msg.is_complete && (
                        <CheckCheck className="h-3 w-3 opacity-60" />
                      )}
                      {!msg.is_read && !msg.is_complete && (
                        <Check className="h-3 w-3 opacity-40" />
                      )}
                    </div>
                    {/* Mark complete button (only for messages from others, not already complete) */}
                    {!isOwn && !msg.is_complete && (
                      <button
                        onClick={() => handleMarkComplete(msg.id)}
                        className="text-[10px] mt-1 opacity-60 hover:opacity-100 flex items-center gap-1 transition-opacity"
                      >
                        <CheckCircle className="h-3 w-3" /> Mark complete
                      </button>
                    )}
                  </div>
                  {/* CR-2220: Message actions dropdown for own messages */}
                  {isOwn && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <MoreVertical className="h-3 w-3" />
                          )}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {msg.is_strikethrough ? (
                          <DropdownMenuItem onClick={() => handleRemoveStrikethrough(msg.id)}>
                            <Undo2 className="h-4 w-4 mr-2" />
                            Remove Strikethrough
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => handleStrikethrough(msg.id)}>
                            <Strikethrough className="h-4 w-4 mr-2" />
                            Strikethrough
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => handleDeleteMessage(msg.id)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t pt-3">
        <div className="flex gap-2">
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            rows={2}
            className="flex-1 resize-none rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          <button
            onClick={handleSend}
            disabled={!newMessage.trim() || sending}
            className="self-end px-3 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}
